import { observable, computed, action } from "mobx";
import { asyncAction } from "mobx-utils";
import Autobind from "autobind-decorator";
import RootStore from "./rootStore";
import Cache from "core/cache";
import CustomEvent from "core/customEvent";
import _ from "lodash";

// sync function disable
import Synchronizer from "component/molecules/viewport/tools/sync/Synchronizer.js";
import stackImagePositionSynchronizer from "component/molecules/viewport/tools/sync/stackImagePositionSynchronizer.js";
import stackImagePositionOffsetSynchronizer from "component/molecules/viewport/tools/sync/stackImagePositionOffsetSynchronizer.js";
import panZoomSynchronizer from "component/molecules/viewport/tools/sync/panZoomSynchronizer.js";
import wwwcSynchronizer from "component/molecules/viewport/tools/sync/wwwcSynchronizer.js";
import updateImageSynchronizer from "component/molecules/viewport/tools/sync/updateImageSynchronizer.js";
import { getEnabledElementById } from "component/toolManager/enabledElements.js";
import Constants from "constants/index";
import Helper from "helper";

/**
 * @class SynchronizationStore
 * @description 요청에 따른 Study 목록을 반환
 */
@Autobind
class SynchronizationStore {
  _rootStore: RootStore; /// rootStore 객체. 다른 Store를 연계할 때 사용

  @observable
  _syncState: string = Constants.Synchronization.SYNC_AUTO;

  @observable
  _syncSlicePositionActive: boolean = true;

  @observable
  _syncZoomAndPanActive: boolean = true;

  @observable
  _syncWindowActive: boolean = true;

  _slicePositionManualSync: any; // for manual
  _slicePositionSync: any;
  _panZoomSync: any;
  _wwwcSync: any;
  _updateImageSync: any;

  constructor(rootStore: RootStore) {
    this._rootStore = rootStore;
    // sync function disable
    this._updateImageSync = new Synchronizer(
      CustomEvent.EVENTS.NEW_IMAGE,
      updateImageSynchronizer
    );
    // sync function disable
    this._slicePositionSync = new Synchronizer(
      CustomEvent.EVENTS.SCROLL_INDEX_CHANGED,
      stackImagePositionSynchronizer
    );
    this._slicePositionManualSync = new Synchronizer(
      CustomEvent.EVENTS.SCROLL_INDEX_CHANGED,
      stackImagePositionOffsetSynchronizer
    );
    this._panZoomSync = new Synchronizer(
      CustomEvent.EVENTS.PAN_ZOOM_CHANGED,
      panZoomSynchronizer
    );
    this._wwwcSync = new Synchronizer(
      CustomEvent.EVENTS.WWWC_CHANGED,
      wwwcSynchronizer
    );

    this.activateSync(false);
  }

  @action
  registerContainerElement(element: HTMLElement) {
    this._updateImageSync.registerEventListener(element);
    this._slicePositionSync.registerEventListener(element);
    this._slicePositionManualSync.registerEventListener(element);
    this._panZoomSync.registerEventListener(element);
    this._wwwcSync.registerEventListener(element);
  }
  @action
  unregisterContainerElement(element: HTMLElement) {
    this._updateImageSync.unregisterEventListener(element);
    this._slicePositionSync.unregisterEventListener(element);
    this._slicePositionManualSync.unregisterEventListener(element);
    this._panZoomSync.unregisterEventListener(element);
    this._wwwcSync.unregisterEventListener(element);
  }
  @action
  synchronizeManualElementByIds(viewIds: number[]) {
    // sync function disable
    (this._slicePositionSync as any).destroy();
    (this._slicePositionManualSync as any).destroy();
    (this._panZoomSync as any).destroy();
    (this._wwwcSync as any).destroy();
    (this._updateImageSync as any).destroy();

    // Source DCMView 찾기
    //let sourceElements: any = [];
    let sourceViewIds: any = [];
    viewIds.forEach((viewId: any, idx: any) => {
      sourceViewIds.push(viewId);
    });
    sourceViewIds.forEach((sourceViewId: number) => {
      let targetViewIds: number[] = [];
      let targetViewElements: any[] = [];
      viewIds.forEach((viewId: any, idx: any) => {
        if (sourceViewId !== viewId) {
          targetViewIds.push(viewId);
          const targetViewElement = getEnabledElementById(viewId);

          if (Helper.Validator.isDefined(targetViewElement) === true) {
            targetViewElements.push(targetViewElement.element);
          } else {
          }

          //targetViewElements.push(targetViewElement);
        }
      });

      const sourceViewElement = getEnabledElementById(sourceViewId).element;
      /// pan/zoom sync
      (this._panZoomSync as any).add(sourceViewElement, targetViewElements);
      /// wwwc sync
      (this._wwwcSync as any).add(sourceViewElement, targetViewElements);
      /// update image sync
      (this._updateImageSync as any).add(sourceViewElement, targetViewElements);
      /// slice position sync
      (this._slicePositionManualSync as any).add(
        sourceViewElement,
        targetViewElements
      );
    });
    this.activateSync(true);
  }

  @action
  synchronizeElements() {
    const {
      _DCMViewModelMap: DCMViewToDataMap,
    } = this._rootStore.gridLayoutStore;

    // sync function disable
    (this._slicePositionSync as any).destroy();
    (this._panZoomSync as any).destroy();
    (this._wwwcSync as any).destroy();
    (this._updateImageSync as any).destroy();
    (this._slicePositionManualSync as any).destroy();

    ///
    /// Source DCMView 찾기
    ///
    let sourceElements: any = [];
    let sourceMapIdxs: any = [];
    DCMViewToDataMap.forEach((value: any, dataMapIdx: any) => {
      const elementStub = getEnabledElementById(dataMapIdx);
      if (!!elementStub === false) {
        return;
      }
      sourceElements.push(elementStub.element);
      sourceMapIdxs.push(dataMapIdx);
    });

    sourceElements.forEach((sourceElement: any, sourceIdx: any) => {
      let targetElements: any[] = [];
      DCMViewToDataMap.forEach((value: any, dataMapIdx: any) => {
        let metadataCache = new Cache.Metadata();
        const targetStudyUID = metadataCache.getStudyInstanceUID(
          value.metadataUID
        );
        const sourceKey = DCMViewToDataMap.get(sourceMapIdxs[sourceIdx]);
        const sourceStudyUID = metadataCache.getStudyInstanceUID(
          sourceKey.metadataUID
        );
        const elementStub = getEnabledElementById(dataMapIdx);
        if (!!elementStub === false) {
          return;
        }
        if (
          sourceStudyUID === targetStudyUID &&
          sourceElement !== elementStub.element
        ) {
          targetElements.push(elementStub.element);
        }
      });

      // sync function disable
      /// slice position sync
      (this._slicePositionSync as any).add(sourceElement, targetElements);
      /// pan/zoom sync
      (this._panZoomSync as any).add(sourceElement, targetElements);
      /// wwwc sync
      (this._wwwcSync as any).add(sourceElement, targetElements);
      /// update image sync
      (this._updateImageSync as any).add(sourceElement, targetElements);
    });
  }

  getSyncState() {
    return this._syncState;
  }

  isSyncActive() {
    switch (this._syncState) {
      case Constants.Synchronization.SYNC_AUTO:
      case Constants.Synchronization.SYNC_MANUAL:
        return true;
      case Constants.Synchronization.SYNC_DISABLE:
        return false;
      default:
        // must throw
        return false;
    }
  }
  @action
  activateSync(active: boolean) {
    const isActive = this.isSyncActive();
    if (isActive === true) {
      this._slicePositionSync.setEnabled(this._syncSlicePositionActive);
      this._panZoomSync.setEnabled(this._syncZoomAndPanActive);
      this._wwwcSync.setEnabled(this._syncWindowActive);
    } else {
      this._slicePositionSync.setEnabled(false);
      this._panZoomSync.setEnabled(false);
      this._wwwcSync.setEnabled(false);
    }
  }

  @action
  reactiveSyncState() {
    if (this._syncState === Constants.Synchronization.SYNC_MANUAL) {
      const activeDCMViewIds = this._rootStore.gridLayoutStore.getActiveDCMViews();
      this._rootStore.synchronizationStore.synchronizeManualElementByIds(
        activeDCMViewIds
      );
    } else if (this._syncState === Constants.Synchronization.SYNC_AUTO) {
      this.synchronizeElements();
    } else if (this._syncState === Constants.Synchronization.SYNC_DISABLE) {
      (this._slicePositionSync as any).destroy();
      (this._panZoomSync as any).destroy();
      (this._wwwcSync as any).destroy();
      (this._updateImageSync as any).destroy();
      (this._slicePositionManualSync as any).destroy();
    }
  }

  @action
  disableSyncState() {
    this._syncState = Constants.Synchronization.SYNC_DISABLE;
    (this._slicePositionSync as any).destroy();
    (this._panZoomSync as any).destroy();
    (this._wwwcSync as any).destroy();
    (this._updateImageSync as any).destroy();
    (this._slicePositionManualSync as any).destroy();
  }

  @action
  toggleSyncState() {
    const currentSyncStateIdx = Constants.Synchronization.SYNC_STATES.indexOf(
      this._syncState
    );
    const nextSyncStateIdx =
      (currentSyncStateIdx + 1) % Constants.Synchronization.SYNC_STATES.length;
    this._syncState = Constants.Synchronization.SYNC_STATES[nextSyncStateIdx];

    if (this._syncState === Constants.Synchronization.SYNC_MANUAL) {
      const activeDCMViewIds = this._rootStore.gridLayoutStore.getActiveDCMViews();
      this._rootStore.synchronizationStore.synchronizeManualElementByIds(
        activeDCMViewIds
      );
    } else if (this._syncState === Constants.Synchronization.SYNC_AUTO) {
      this.synchronizeElements();
    } else if (this._syncState === Constants.Synchronization.SYNC_DISABLE) {
      (this._slicePositionSync as any).destroy();
      (this._panZoomSync as any).destroy();
      (this._wwwcSync as any).destroy();
      (this._updateImageSync as any).destroy();
      (this._slicePositionManualSync as any).destroy();
    }
  }
  @action
  toggleSyncActive() {
    const isActive = this.isSyncActive();
    if (isActive === true) {
      this._slicePositionSync.setEnabled(this._syncSlicePositionActive);
      this._panZoomSync.setEnabled(this._syncZoomAndPanActive);
      this._wwwcSync.setEnabled(this._syncWindowActive);
    } else {
      this._slicePositionSync.setEnabled(false);
      this._panZoomSync.setEnabled(false);
      this._wwwcSync.setEnabled(false);
    }
  }
  isSupportedSynchronizationOnActive() {
    const activeDCMViewIds = this._rootStore.gridLayoutStore.getActiveDCMViews();

    const numOfActiveDCMViews = activeDCMViewIds.length;

    let isSupport = true;
    for (let i = 0; i < numOfActiveDCMViews; i++) {
      const viewId = activeDCMViewIds[i];
      const {
        _DCMViewModelMap: DCMViewToDataMap,
      } = this._rootStore.gridLayoutStore;
      const DCMViewData = DCMViewToDataMap.get(viewId);
      if (Helper.Validator.isDefined(DCMViewData) === false) {
        continue;
      }
      const metadataId = DCMViewData.metadataUID;
      let metadataCache = new Cache.Metadata();
      isSupport = metadataCache.isSupportedSync(metadataId) ? true : false;

      if (isSupport === false) {
        break;
      }
    }

    return isSupport;
  }
  isSyncSlicePositionActive() {
    return this._syncSlicePositionActive;
  }

  @action
  activateSyncSlicePosition(active: boolean) {
    this._syncSlicePositionActive = active;
    const isActive = this.isSyncActive();
    if (isActive === true) {
      this._slicePositionSync.setEnabled(this._syncSlicePositionActive);
    }
  }
  @action
  toggleSyncSlicePositionActive() {
    this._syncSlicePositionActive = !this._syncSlicePositionActive;
    const isActive = this.isSyncActive();
    if (isActive === true) {
      this._slicePositionSync.setEnabled(this._syncSlicePositionActive);
    }
  }

  isSyncZoomAndPanActive() {
    return this._syncZoomAndPanActive;
  }
  @action
  activateSyncZoomAndPan(active: boolean) {
    this._syncZoomAndPanActive = active;
    const isActive = this.isSyncActive();
    if (isActive === true) {
      this._panZoomSync.setEnabled(this._syncZoomAndPanActive);
    }
  }
  @action
  toggleSyncZoomAndPanActive() {
    this._syncZoomAndPanActive = !this._syncZoomAndPanActive;
    const isActive = this.isSyncActive();
    if (isActive === true) {
      this._panZoomSync.setEnabled(this._syncZoomAndPanActive);
    }
  }

  isSyncWindowActive() {
    return this._syncWindowActive;
  }

  @action
  activateSyncWindow(active: boolean) {
    this._syncWindowActive = active;
    const isActive = this.isSyncActive();
    if (isActive === true) {
      this._wwwcSync.setEnabled(this._syncWindowActive);
    }
  }
  @action
  toggleSyncWindow() {
    this._syncWindowActive = !this._syncWindowActive;
    const isActive = this.isSyncActive();
    if (isActive === true) {
      this._wwwcSync.setEnabled(this._syncWindowActive);
    }
  }
}

export default SynchronizationStore;
